import { createApi } from '@reduxjs/toolkit/query/react';

import {
  fetchWithAuthBaseQuery,
  validateAndTransformResponse,
} from '#shared/api';

import {
  asDateRangeDays,
  asEmployeeReservationNodes,
  asResponseResult,
  readStatus,
} from './nopResourcesEnvelope';
import {
  nopConsolesResponseSchema,
  nopEmployeeReservationNodesResponseSchema,
  nopEmployeesResponseSchema,
  nopHubsResponseSchema,
  nopShiftDaysResponseSchema,
} from './schemas/nopResourcesResponse.schema';

import type {
  NopConsole,
  NopEmployee,
  NopEmployeeReservationsBody,
  NopHub,
  NopResourcesDateRangeRead,
  NopResourcesRead,
  NopShiftPartition,
} from '../model/types';

const paths = {
  hubs: 'api/nop/hubs',
  devices: 'api/nop/devices',
  csShifts: 'api/nop/csShifts',
  employees: 'api/nop/employees',
  employeeReservations: 'api/nop/employeeReservations',
} as const;

export interface NopCSShiftsArgs {
  /** NOP's own `hub.id`. Required: the whole point is one hub's partition. */
  hub: string;
  /** Inclusive `YYYY-MM-DD`. At most 40 days apart, per the BE's span cap. */
  from: string;
  to: string;
}

export interface NopEmployeeReservationsArgs {
  /** Inclusive `YYYY-MM-DD`. At most 40 days apart, the endpoint's span cap. */
  from: string;
  to: string;
}

/**
 * The NOP resources boundary (BE ADR CODE-012).
 *
 * A separate `createApi` from `hubsResourceReportApi` rather than more endpoints
 * on it: the two serve the same upstream data under different contracts and are
 * meant to coexist while consumers migrate one at a time. Sharing a reducer path
 * would make "which shape is this cache entry" a runtime question.
 */
export const nopResourcesApi = createApi({
  reducerPath: 'nopResourcesApi',
  baseQuery: fetchWithAuthBaseQuery,
  refetchOnReconnect: true,
  keepUnusedDataFor: 50,

  endpoints: build => ({
    getNopHubs: build.query({
      query: () => paths.hubs,
      transformResponse: (response: NopResourcesRead<NopHub[]>) => ({
        ...validateAndTransformResponse(
          nopHubsResponseSchema,
          asResponseResult(response),
          value => value?.id,
          'nopHubs',
        ),
        status: readStatus(response),
      }),
    }),

    /**
     * One hub's consoles, or every hub's when `hub` is omitted.
     *
     * The dashboard always passes a hub: that is what makes the read one `HGET`
     * of a few KB rather than all 19 hubs' consoles.
     */
    getNopDevices: build.query({
      query: (hub: string | undefined) =>
        hub ? `${paths.devices}?hub=${encodeURIComponent(hub)}` : paths.devices,
      transformResponse: (response: NopResourcesRead<NopConsole[]>) => ({
        ...validateAndTransformResponse(
          nopConsolesResponseSchema,
          asResponseResult(response),
          value => value?.serialNumber ?? value?.id,
          'nopDevices',
        ),
        status: readStatus(response),
      }),
    }),

    /**
     * The whole employee roster.
     *
     * Whole, not per-id: the endpoint takes a single `?id=`, and the panel needs
     * every rostered specialist's badges, rest period and case reservation. One
     * 1.4 MB read on a 60s poll beats twenty round trips, and it is one `HGETALL`
     * on the BE.
     */
    getNopEmployees: build.query({
      query: () => paths.employees,
      transformResponse: (response: NopResourcesRead<NopEmployee[]>) => ({
        ...validateAndTransformResponse(
          nopEmployeesResponseSchema,
          asResponseResult(response),
          value => value?.id,
          'nopEmployees',
        ),
        status: readStatus(response),
      }),
    }),

    /**
     * One hub's clinical specialist shifts over a date range.
     *
     * Each date keeps its own status rather than the range being merged, because
     * a date outside the BE's swept window reads `absent` — never fetched — which
     * is a different answer from "nobody was scheduled". There is no on-demand
     * fill, so that case is reachable at a month's edges.
     */
    getNopCSShifts: build.query({
      query: ({ hub, from, to }: NopCSShiftsArgs) =>
        `${paths.csShifts}?hub=${encodeURIComponent(hub)}&from=${from}&to=${to}`,
      transformResponse: (
        response: NopResourcesDateRangeRead<NopShiftPartition> | undefined,
      ) =>
        validateAndTransformResponse(
          nopShiftDaysResponseSchema,
          {
            data: asDateRangeDays(response),
            errors: null,
            expires: null,
            dataGeneratedAt: null,
            errorsGeneratedAt: null,
          },
          value => value?.date,
          'nopCSShifts',
        ),
    }),

    /**
     * Clinical specialists' `OnCase` and `OnRest` nodes over a date range, for every hub.
     *
     * A bare `{ employees }` body rather than a NOP resources read, so there is
     * no swept window and no `absent` status.
     */
    getNopEmployeeReservations: build.query({
      query: ({ from, to }: NopEmployeeReservationsArgs) =>
        `${paths.employeeReservations}?from=${from}&to=${to}`,
      transformResponse: (response: NopEmployeeReservationsBody | undefined) =>
        validateAndTransformResponse(
          nopEmployeeReservationNodesResponseSchema,
          {
            data: asEmployeeReservationNodes(response),
            errors: null,
            expires: null,
            dataGeneratedAt: null,
            errorsGeneratedAt: null,
          },
          value => value?.nodeId,
          'nopEmployeeReservations',
        ),
    }),
  }),
});

export const {
  useGetNopHubsQuery,
  useGetNopDevicesQuery,
  useGetNopCSShiftsQuery,
  useGetNopEmployeesQuery,
  useGetNopEmployeeReservationsQuery,
} = nopResourcesApi;
